import api from './api'; // Import the configured api instance
import { getValidTenantId } from '../utils/tenantUtils';

// Path to members endpoints
const ENDPOINT = '/members';

// Helper function to safely extract data from axios responses
const extractResponseData = (axiosResponse) => {
  if (!axiosResponse || !axiosResponse.data) {
    console.warn('Empty response received:', axiosResponse);
    return null;
  }
  return axiosResponse.data;
};

// Add token handling before getAllMembers
const getAuthToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.warn('No auth token found in localStorage');
  }
  return token;
};

// Get a single member by ID
const getMember = async (memberId) => {
  try {
    console.log(`Fetching member with ID: ${memberId}`);
    
    // Get tenant ID using the standardized function
    const tenantId = getValidTenantId();
    
    // Configure request with proper headers
    const headers = {};
    
    // Add authorization if available
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add tenant ID if available
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    const response = await api.get(`${ENDPOINT}/${memberId}`, { headers });
    
    // Log success for debugging
    console.log('Member fetched successfully:', response);
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to fetch member: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get all members with pagination, filtering, and search options
const getAllMembers = async (page = 1, limit = 10, search = '', filters = {}) => {
  try {
    // Add debug logging for search terms
    if (search) {
      console.log(`Searching for members with term: "${search}"`);
    }
    
    const params = { 
      page, 
      limit,
      ...(search && { search }),
      ...(filters.status && { status: filters.status }),
      ...(filters.membershipType && { membershipType: filters.membershipType }),
      ...(filters.tenant && { tenantId: filters.tenant }),
      populate: 'userId' // Add this to request populated user data
    };
    
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Cross-Tenant': 'true' // Always include this header for member listing
    };

    console.log('getAllMembers request config:', { headers, params });
    
    const axiosResponse = await api.get('/members', { 
      headers,
      params 
    });

    // Add debug logging
    console.log('getAllMembers response:', axiosResponse?.data);
    
    if (!axiosResponse?.data?.success) {
      throw new Error('Invalid response format');
    }

    return axiosResponse.data;
  } catch (error) {
    console.error('getAllMembers error:', error);
    throw error;
  }
};

// Get all members with custom headers - this version allows explicit header passing
const getAllMembersWithHeaders = async (page = 1, limit = 10, search = '', filters = {}, headers = {}) => {
  try {
    // Add debug logging for search terms and pagination
    console.log(`Fetching members with page=${page}, limit=${limit}, search="${search}"`);
    console.log('Filters:', filters);
    console.log('Custom headers provided:', headers);
    
    const params = { 
      page, 
      limit,
      ...(search && { search }),
      ...(filters.status && { status: filters.status }),
      ...(filters.membershipType && { membershipType: filters.membershipType }),
      ...(filters.tenant && { tenantId: filters.tenant }),
      populate: 'userId' // Always populate user data
    };
    
    // If no auth token provided in headers, get it from localStorage
    if (!headers.Authorization) {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }
      headers.Authorization = `Bearer ${token}`;
    }

    // Always include cross-tenant header for member listing
    headers['X-Cross-Tenant'] = 'true';
    
    console.log('getAllMembersWithHeaders request config:', { headers, params });
    
    const axiosResponse = await api.get('/members', { 
      headers,
      params 
    });

    // Add debug logging
    console.log('getAllMembersWithHeaders response:', axiosResponse?.data);
    
    return axiosResponse.data;
  } catch (error) {
    console.error('getAllMembersWithHeaders error:', error);
    throw error;
  }
};

// Create a new member
const createMember = (memberData) => {
  // Make sure role is included in the data we send to the API
  console.log('Creating member with data:', memberData);
  return api.post('/members', memberData);
};

// Update an existing member
const updateMember = (id, memberData) => {
  // Log the update data to help with debugging
  console.log(`Updating member ${id} with data:`, memberData);
  return api.put(`/members/${id}`, memberData);
};

// Delete a member
const deleteMember = async (memberId) => {
  try {
    return await api.delete(`${ENDPOINT}/${memberId}`);
  } catch (error) {
    console.error(`Error deleting member ${memberId}:`, error);
    error.message = 'Failed to delete member: ' + (error.message || 'Server error');
    throw error;
  }
};

// Search for members
const searchMembers = async (query) => {
  return await api.get(`${ENDPOINT}/search`, { params: { query } });
};

// Add payment to a member
const addPayment = async (memberId, paymentData) => {
  try {
    // Prepare the request data
    const requestData = {
      ...paymentData,
      // Ensure amount is a number
      amount: parseFloat(paymentData.amount)
    };

    // Make the API request
    const response = await api.post(`${ENDPOINT}/${memberId}/payments`, requestData);
    
    // Log success for debugging
    console.log('Payment added successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error adding payment for member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to add payment: ' + (error.message || 'Server error');
    throw error;
  }
};

// Apply payment to a member's debt
const applyPaymentToDebt = async (memberId, paymentData, debtId) => {
  try {
    console.log(`Applying payment to debt ${debtId} for member ${memberId}:`, {
      ...paymentData,
      amount: `$${paymentData.amount}`
    });
    
    // Prepare the request data with debt application info
    const requestData = {
      ...paymentData,
      // Ensure amount is a number
      amount: parseFloat(paymentData.amount),
      // Add debt application data
      applyToDebt: true,
      debtId: debtId
    };
    
    // Make the API request
    const response = await api.post(`${ENDPOINT}/${memberId}/payments`, requestData);
    
    // Log success for debugging
    console.log('Debt payment applied successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error applying payment to debt for member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to apply payment to debt: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get current member profile (for logged-in member)
const getCurrentMemberProfile = async (config = {}) => {
  try {
    console.log('Fetching current member profile');
    
    // Get token directly
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token available for profile fetch');
      throw new Error('User identification required to access profile. Please login again.');
    }
    
    // Get user ID from localStorage or context
    const userId = localStorage.getItem('userId');
    if (!userId) {
      console.warn('No user ID found in localStorage');
    } else {
      console.log('Found userId in localStorage:', userId);
    }
    
    // Build request headers explicitly to ensure authentication
    const headers = {
      ...config.headers,
      'Authorization': `Bearer ${token}`
    };
    
    // Get tenant ID using the standardized function
    const tenantId = getValidTenantId();
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    console.log('Making profile request with headers:', {
      hasAuthToken: !!headers.Authorization,
      tenantId: headers['X-Tenant-ID'] || 'not set',
      userId: headers['X-User-ID'] || 'not set'
    });
    
    // Try the proper endpoint first - use /profile instead of /me
    try {
      // Make the API request to get the profile using the dedicated profile endpoint
      const response = await api.get(`${ENDPOINT}/profile`, { headers });
      console.log('Profile fetch successful using /profile endpoint:', response);
      return response;
    } catch (profileError) {
      console.warn('Failed to fetch profile using /profile endpoint:', profileError.message);
      
      // Try alternative /me endpoint as fallback
      try {
        console.log('Attempting alternative endpoint: /me');
        const response = await api.get(`${ENDPOINT}/me`, { headers });
        console.log('Profile fetch successful using /me endpoint:', response);
        return response;
      } catch (meError) {
        console.error('Both profile endpoints failed:', meError);
        throw meError;
      }
    }
  } catch (error) {
    console.error('Error fetching member profile:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to load your profile data. ' + 
      (error.response?.data?.message || error.message || 'Server error');
    throw error;
  }
};

// Update member profile (for logged-in member)
const updateCurrentMemberProfile = async (profileData) => {
  try {
    console.log('Updating current member profile');
    
    // Get token directly to ensure authentication
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token available for profile update');
      throw new Error('User not authenticated. Please login again.');
    }

    // Get the current auth user data
    const authUserStr = localStorage.getItem('authUser');
    let userId = localStorage.getItem('userId');
    
    // Try to get userId from authUser if not found directly
    if (!userId && authUserStr) {
      try {
        const authUser = JSON.parse(authUserStr);
        userId = authUser.id || authUser._id;
      } catch (e) {
        console.error('Error parsing authUser:', e);
      }
    }
    
    // Get tenant ID using the standardized function
    const tenantId = getValidTenantId();
    
    // Build request headers explicitly to ensure authentication
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    if (userId) {
      headers['X-User-ID'] = userId;
    }
    
    console.log('Making profile update request with headers:', {
      hasAuthToken: !!headers.Authorization,
      tenantId: headers['X-Tenant-ID'] || 'not set',
      userId: headers['X-User-ID'] || 'not set'
    });
    
    // Make the API request to update the profile with explicit headers
    const response = await api.put(`${ENDPOINT}/profile`, profileData, { headers });
    
    // Update local storage to keep user data in sync
    if (response && response.data && response.data.success) {
      try {
        // Update authUser in localStorage
        if (authUserStr) {
          const authUser = JSON.parse(authUserStr);
          const updatedUser = { 
            ...authUser,
            firstName: profileData.firstName || authUser.firstName,
            lastName: profileData.lastName || authUser.lastName
          };
          localStorage.setItem('authUser', JSON.stringify(updatedUser));
          
          // Also update the 'user' item if it exists
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const user = JSON.parse(userStr);
            user.firstName = profileData.firstName || user.firstName;
            user.lastName = profileData.lastName || user.lastName;
            localStorage.setItem('user', JSON.stringify(user));
          }
        }
      } catch (e) {
        console.warn('Error updating local storage after profile update:', e);
      }
    }
    
    // Log success for debugging
    console.log('Profile update successful:', response);
    
    return response;
  } catch (error) {
    console.error('Error updating member profile:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to update your profile: ' + (error.response?.data?.message || error.message || 'Server error');
    throw error;
  }
};

// Update member's user account (name, email, etc.)
const updateMemberAccount = async (memberId, userData) => {
  return await api.put(`${ENDPOINT}/${memberId}/account`, userData);
};

// Get member's payment history with better error handling
const getMemberPaymentHistory = async (memberId) => {
  try {
    const response = await api.get(`${ENDPOINT}/${memberId}/payments`);
    return response;
  } catch (error) {
    console.error(`Error fetching payment history for member ${memberId}:`, error);
    
    // Try alternate endpoint if first one fails
    try {
      console.log(`Trying alternate payment endpoint for member ${memberId}`);
      const altResponse = await api.get(`/payments/member/${memberId}`);
      return altResponse;
    } catch (altError) {
      console.error(`Alternate payment endpoint also failed for member ${memberId}:`, altError);
      throw error; // Throw the original error
    }
  }
};

// Get member by membership number
const getMemberByMembershipNumber = async (membershipNumber) => {
  return await api.get(`${ENDPOINT}/number/${membershipNumber}`);
};

// Get dashboard stats
const getDashboardStats = async () => {
  try {
    const axiosResponse = await api.get(`${ENDPOINT}/stats/dashboard`);
    
    // Check for valid response
    if (!axiosResponse || !axiosResponse.data) {
      console.error('Empty or invalid dashboard stats response:', axiosResponse);
      throw new Error('Invalid response from dashboard stats API');
    }
    
    // Return the data portion of the response
    return axiosResponse.data;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

// Get membership analytics data with timeframe options
const getMembershipAnalytics = async (timeframe = '1m') => {
  try {
    console.log(`Fetching membership analytics with timeframe: ${timeframe}`);
    
    const axiosResponse = await api.get(`${ENDPOINT}/analytics/membership`, {
      params: { timeframe }
    });
    
    // Check for valid response
    if (!axiosResponse || !axiosResponse.data) {
      console.error('Empty or invalid membership analytics response:', axiosResponse);
      throw new Error('Invalid response from membership analytics API');
    }
    
    console.log('Membership analytics data received:', axiosResponse.data);
    
    // Return the data portion of the response
    return axiosResponse.data;
  } catch (error) {
    console.error('Error fetching membership analytics:', error);
    
    // Add more detailed error information
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to load membership analytics: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get member growth trend data
const getMemberGrowthTrend = async (timeframe = '1y') => {
  try {
    console.log(`Fetching member growth trend with timeframe: ${timeframe}`);
    
    const axiosResponse = await api.get(`${ENDPOINT}/analytics/growth`, {
      params: { timeframe }
    });
    
    // Check for valid response
    if (!axiosResponse || !axiosResponse.data) {
      console.error('Empty or invalid growth trend response:', axiosResponse);
      return [];
    }
    
    return axiosResponse.data;
  } catch (error) {
    console.error('Error fetching member growth trend:', error);
    error.message = 'Failed to load growth trend: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get membership distribution data
const getMembershipDistribution = async () => {
  try {
    console.log('Fetching membership distribution data');
    
    const axiosResponse = await api.get(`${ENDPOINT}/analytics/distribution`);
    
    // Check for valid response
    if (!axiosResponse || !axiosResponse.data) {
      console.error('Empty or invalid distribution data response:', axiosResponse);
      return [];
    }
    
    return axiosResponse.data;
  } catch (error) {
    console.error('Error fetching membership distribution:', error);
    error.message = 'Failed to load membership distribution: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get membership retention rate
const getMemberRetentionRate = async (timeframe = '1y') => {
  try {
    console.log(`Fetching retention rate with timeframe: ${timeframe}`);
    
    const axiosResponse = await api.get(`${ENDPOINT}/analytics/retention`, {
      params: { timeframe }
    });
    
    // Check for valid response
    if (!axiosResponse || !axiosResponse.data) {
      console.error('Empty or invalid retention rate response:', axiosResponse);
      return 0;
    }
    
    return axiosResponse.data.rate || 0;
  } catch (error) {
    console.error('Error fetching retention rate:', error);
    error.message = 'Failed to load retention rate: ' + (error.message || 'Server error');
    throw error;
  }
};

// Change member password
const changePassword = async (currentPassword, newPassword) => {
  try {
    console.log('Changing member password');
    
    // Make the API request to change the password
    const response = await api.post(`${ENDPOINT}/password`, { 
      currentPassword, 
      newPassword 
    });
    
    // Log success for debugging (without sensitive data)
    console.log('Password change successful:', response);
    
    return response;
  } catch (error) {
    console.error('Error changing password:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to change password: ' + (error.response?.data?.message || error.message || 'Server error');
    throw error;
  }
};

// Upload profile picture
const uploadProfilePicture = async (formData) => {
  try {
    // Check if we received FormData or need to create it
    const isFormData = formData instanceof FormData;
    let dataToSend;
    
    // If we received a file instead of FormData, create the FormData
    if (!isFormData && formData) {
      console.log("Creating new FormData with file object:", formData.name);
      dataToSend = new FormData();
      dataToSend.append('profilePicture', formData);
    } else if (isFormData) {
      console.log("Using provided FormData:", Array.from(formData.keys()));
      dataToSend = formData;
    } else {
      // No form data or file provided
      throw new Error('No image file provided. Please select an image to upload.');
    }
    
    // Verify FormData has the file
    if (dataToSend instanceof FormData && !dataToSend.has('profilePicture')) {
      console.error("FormData missing 'profilePicture' field");
      throw new Error('No image file provided. Please select an image to upload.');
    }
    
    // Get auth token to ensure proper authentication
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required to upload profile picture');
    }
    
    // Log what we're sending for debugging
    console.log("Uploading profile picture:", { 
      hasToken: !!token,
      formDataKeys: Array.from(dataToSend.keys()),
      formDataHasFile: dataToSend.has('profilePicture')
    });
    
    // IMPORTANT: Create a new custom config to ensure proper handling of FormData
    const config = {
      headers: {
        'Authorization': `Bearer ${token}`,
        // DO NOT set Content-Type here - axios will set the correct one with boundary
      },
      timeout: 30000 // 30 second timeout
    };
    
    // Use the API instance with the custom config
    const response = await api.post(`${ENDPOINT}/profile/picture`, dataToSend, config);
    
    // Log success for debugging
    console.log('Profile picture upload successful:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    // Make error message user-friendly
    const errorMessage = 
      error.message === 'No image file provided. Please select an image to upload.' ? 
      error.message : 
      'Failed to upload profile picture: ' + (error.response?.data?.message || error.message || 'Server error');
    
    throw new Error(errorMessage);
  }
};

// Delete profile picture
const deleteProfilePicture = async () => {
  try {
    console.log('Deleting profile picture');
    
    // Make the API request
    const response = await api.delete(`${ENDPOINT}/profile/picture`);
    
    // Log success for debugging
    console.log('Profile picture deletion successful:', response);
    
    return response.data;
  } catch (error) {
    console.error('Error deleting profile picture:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to delete profile picture: ' + (error.message || 'Server error');
    throw error;
  }
};

// Upload profile picture for a specific member (admin function)
const uploadMemberProfilePicture = async (memberId, file) => {
  // Create form data to send the file
  const formData = new FormData();
  formData.append('profilePicture', file);
  
  // Important: For multipart/form-data, let the browser set the Content-Type with boundary
  const headers = {
    'Content-Type': undefined
  };
  
  return await api.post(`${ENDPOINT}/${memberId}/picture`, formData, { headers });
};

// Delete profile picture for a specific member (admin function)
const deleteMemberProfilePicture = async (memberId) => {
  return await api.delete(`${ENDPOINT}/${memberId}/picture`);
};

// Add new method to get members for directory
const getDirectoryMembers = async (page = 1, limit = 10, search = '', filters = {}) => {
  try {
    // Get tenant ID using the standardized function
    const tenantId = getValidTenantId();
    
    if (!tenantId) {
      console.error('No tenant ID found when fetching directory members');
      throw new Error('Tenant ID is required for directory access');
    }

    // Check if we're authenticated
    const token = localStorage.getItem('token');
    
    console.log('Fetching directory members with:', { 
      authenticated: !!token, 
      tenantId,
      page,
      limit,
      search
    });
    
    // Build params for the request
    const params = {
      page,
      limit,
      search,
      directory: true,
      tenantId,  // Always explicitly include tenantId
      ...filters
    };
    
    // If no auth token, immediately return demo data
    if (!token) {
      console.log('No auth token - providing demo directory data');
      
      // Create demo data with proper pagination info
      return {
        data: generateDemoMembers(),
        pagination: {
          page: 1,
          limit: 10,
          pages: 1, 
          total: 3
        },
        demo: true
      };
    }
    
    // Configure headers with authorization
    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Tenant-ID': tenantId
    };
    
    // Make the API call with full configuration
    try {
      console.log('Attempting to call /members/directory endpoint');
      const response = await api.get('/members/directory', { 
        params,
        headers
      });
      console.log('Directory endpoint succeeded:', response.data);
      return response.data;
    } catch (endpointError) {
      console.warn('Directory-specific endpoint failed:', endpointError);
      
      if (endpointError.response?.status === 401) {
        console.log('Authentication error - providing demo directory data');
        return {
          data: generateDemoMembers(),
          pagination: {
            page: 1,
            limit: 10,
            pages: 1,
            total: 3
          },
          demo: true
        };
      }
      
      // Try fallback to regular members endpoint with directory flag
      try {
        console.log('Attempting to call /members endpoint with directory flag');
        const response = await api.get('/members', { 
          params,
          headers
        });
        console.log('Regular members endpoint with directory flag succeeded:', response.data);
        return response.data;
      } catch (membersError) {
        console.warn('Both endpoints failed:', membersError);
        
        // Last resort - return demo data
        if (membersError.response?.status === 401) {
          console.log('Authentication error on fallback - providing demo directory data');
          return {
            data: generateDemoMembers(),
            pagination: {
              page: 1,
              limit: 10,
              pages: 1,
              total: 3
            },
            demo: true
          };
        }
        
        throw membersError;
      }
    }
  } catch (error) {
    console.error('Error fetching directory members:', error);
    
    // Return demo data for any error
    return {
      data: generateDemoMembers(),
      pagination: {
        page: 1,
        limit: 10,
        pages: 1,
        total: 3
      },
      error: error.message,
      demo: true
    };
  }
};

// Helper function for demo data
const generateDemoMembers = () => {
  return [
    {
      _id: "demo1",
      membershipType: "Regular",
      status: "active",
      joinDate: "2023-01-15",
      userId: {
        firstName: "John",
        lastName: "Smith",
        email: "john@example.com"
      }
    },
    {
      _id: "demo2",
      membershipType: "Premium",
      status: "active",
      joinDate: "2022-11-05",
      userId: {
        firstName: "Sarah",
        lastName: "Johnson",
        email: "sarah@example.com"
      }
    },
    {
      _id: "demo3",
      membershipType: "Regular",
      status: "active",
      joinDate: "2023-03-22",
      userId: {
        firstName: "Michael",
        lastName: "Williams",
        email: "michael@example.com"
      }
    }
  ];
};

// Add new method to transfer member between tenants
const transferMember = async (memberId, newTenantId) => {
  try {
    const response = await api.put(`${ENDPOINT}/${memberId}/transfer`, {
      newTenantId
    });
    return response.data;
  } catch (error) {
    console.error('Error transferring member:', error);
    throw error;
  }
};

// Add debt to a member
const addDebt = async (memberId, debtData) => {
  try {
    console.log(`Adding debt for member ${memberId}:`, {
      ...debtData,
      amount: `$${debtData.amount}`
    });
    
    const response = await api.post(`${ENDPOINT}/${memberId}/debts`, debtData);
    
    // Log success for debugging
    console.log('Debt added successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error adding debt for member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to add debt: ' + (error.message || 'Server error');
    throw error;
  }
};

// Delete debt from a member
const deleteDebt = async (memberId, debtId) => {
  try {
    console.log(`Deleting debt ${debtId} for member ${memberId}`);
    
    const response = await api.delete(`${ENDPOINT}/${memberId}/debts/${debtId}`);
    
    // Log success for debugging
    console.log('Debt deleted successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error deleting debt ${debtId} for member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to delete debt: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get all debts for a member
const getMemberDebts = async (memberId) => {
  try {
    console.log(`Fetching debts for member ${memberId}`);
    
    const response = await api.get(`${ENDPOINT}/${memberId}/debts`);
    
    // Log success for debugging
    console.log('Debts fetched successfully:', response);
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching debts for member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to fetch debts: ' + (error.message || 'Server error');
    throw error;
  }
};

// Update debt status
const updateDebtStatus = async (memberId, debtId, status) => {
  try {
    console.log(`Updating debt ${debtId} status to ${status} for member ${memberId}`);
    
    const response = await api.patch(`${ENDPOINT}/${memberId}/debts/${debtId}`, { status });
    
    // Log success for debugging
    console.log('Debt status updated successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error updating debt status for member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to update debt status: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get current member's debts (for logged-in member)
const getCurrentMemberDebts = async (config = {}) => {
  try {
    console.log('Fetching current member debts');
    
    // Get token directly if not provided in config
    const token = config.headers?.Authorization?.replace('Bearer ', '') || localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token available for debts fetch');
      throw new Error('User identification required to access debts. Please login again.');
    }
    
    // Build request headers explicitly to ensure authentication
    const headers = {
      ...config.headers,
      'Authorization': `Bearer ${token}`
    };
    
    // Get tenant ID using the standardized function or from config
    const tenantId = config.headers?.['X-Tenant-ID'] || getValidTenantId();
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    // Get user ID from config or localStorage
    const userId = config.headers?.['X-User-ID'] || localStorage.getItem('userId');
    if (userId) {
      headers['X-User-ID'] = userId;
    }
    
    console.log('Making debts request with headers:', {
      hasAuthToken: !!headers.Authorization,
      tenantId: headers['X-Tenant-ID'] || 'not set',
      userId: headers['X-User-ID'] || 'not set'
    });
    
    // Make the API request to get the debts
    const response = await api.get(`${ENDPOINT}/me/debts`, { headers });
    
    // Log success for debugging
    console.log('Member debts fetched successfully:', response);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching member debts:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to load your debt information. ' + 
      (error.response?.data?.message || error.message || 'Server error');
    throw error;
  }
};

// Bulk add debts to multiple members
const bulkAddDebts = async (memberIds, debtData) => {
  try {
    console.log('BULK ADD DEBTS - Raw input data:', {
      memberIds,
      debtData
    });

    // Validate required fields explicitly before sending
    if (!debtData.amount || isNaN(parseFloat(debtData.amount))) {
      console.error('VALIDATION ERROR: amount is missing or not a number:', debtData.amount);
      throw new Error('Amount is required and must be a valid number');
    }
    
    if (!debtData.category) {
      console.error('VALIDATION ERROR: category is missing');
      throw new Error('Category is required');
    }
    
    if (!debtData.dueDate) {
      console.error('VALIDATION ERROR: dueDate is missing');
      throw new Error('Due date is required');
    }
    
    // Format the debt data in the exact structure expected by the backend API
    const formattedDebtData = {
      amount: parseFloat(debtData.amount),
      category: debtData.category,
      description: debtData.description || '',
      dueDate: debtData.dueDate
    };
    
    // Log the formatted data
    console.log('BULK ADD DEBTS - Formatted debt data:', formattedDebtData);
    
    // Ensure memberIds are strings to avoid any ObjectId validation issues
    const validatedMemberIds = memberIds.map(id => id.toString());
    
    // Get tenant ID using the standardized function
    const tenantId = getValidTenantId();
    
    // Build request payload
    const requestPayload = {
      memberIds: validatedMemberIds,
      debtData: formattedDebtData
    };
    
    // Log the exact payload being sent to API
    console.log('BULK ADD DEBTS - Sending API request with payload:', JSON.stringify(requestPayload));
    
    // Get token directly to ensure authentication
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token available for bulk debt assignment');
      throw new Error('User not authenticated. Please login again.');
    }
    
    // Configure request with explicit headers
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    // Get user ID from localStorage
    const userId = localStorage.getItem('userId');
    if (userId) {
      headers['X-User-ID'] = userId;
    }
    
    console.log('Making bulk debt request with headers:', {
      hasAuthToken: !!headers.Authorization,
      tenantId: headers['X-Tenant-ID'] || 'not set',
      userId: headers['X-User-ID'] || 'not set'
    });
    
    // Make the API request with proper headers
    const response = await api.post(`${ENDPOINT}/bulk/debts`, requestPayload, { headers });
    
    // Log success for debugging
    console.log('Bulk debts added successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error adding bulk debts:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
      console.error('Request configuration:', error.config);
      
      if (error.config?.data) {
        try {
          const requestData = JSON.parse(error.config.data);
          console.error('Request data sent to API:', requestData);
          
          // Check if debtData was properly formatted in the request
          if (requestData.debtData) {
            console.error('Debt data in request:', requestData.debtData);
            console.error('Due date format in request:', typeof requestData.debtData.dueDate);
          }
        } catch (parseError) {
          console.error('Error parsing request data:', parseError);
        }
      }
      
      // Check for authentication errors
      if (error.response.status === 401) {
        throw new Error('Your session has expired. Please login again to continue.');
      }
      
      // Check for validation errors
      if (error.response.status === 400) {
        const errorMsg = error.response.data.message || 'Validation error with member IDs or debt data';
        throw new Error(errorMsg);
      }
    }
    
    error.message = 'Failed to add bulk debts: ' + (error.response?.data?.message || error.message || 'Server error');
    throw error;
  }
};

// Bulk update debts
const bulkUpdateDebtStatus = async (debtIds, status) => {
  try {
    console.log(`Updating ${debtIds.length} debts status to ${status}`);
    
    const response = await api.patch(`${ENDPOINT}/bulk/debts/status`, {
      debtIds,
      status
    });
    
    // Log success for debugging
    console.log('Bulk debt status updated successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error updating bulk debt status:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to update bulk debt status: ' + (error.message || 'Server error');
    throw error;
  }
};

// Bulk delete debts
const bulkDeleteDebts = async (debtIds) => {
  try {
    console.log(`Deleting ${debtIds.length} debts`);
    
    const response = await api.delete(`${ENDPOINT}/bulk/debts`, {
      data: { debtIds }
    });
    
    // Log success for debugging
    console.log('Bulk debts deleted successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error deleting bulk debts:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to delete bulk debts: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get all debts across all members (for admin)
const getAllDebts = async (filters = {}) => {
  try {
    console.log('Fetching all debts with filters:', filters);
    
    const response = await api.get(`${ENDPOINT}/debts`, {
      params: filters
    });
    
    // Log success for debugging
    console.log('All debts fetched successfully:', response);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching all debts:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to fetch debts: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get debt analytics (total, paid, outstanding, etc.)
const getDebtAnalytics = async () => {
  try {
    console.log('Fetching debt analytics');
    
    const response = await api.get(`${ENDPOINT}/analytics/debts`);
    
    // Log success for debugging
    console.log('Debt analytics fetched successfully:', response);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching debt analytics:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to fetch debt analytics: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get payment analytics by category
const getPaymentAnalytics = async (timeframe = 'all') => {
  try {
    console.log(`Fetching payment analytics with timeframe: ${timeframe}`);
    
    const response = await api.get(`${ENDPOINT}/analytics/payments`, {
      params: { timeframe }
    });
    
    // Log success for debugging
    console.log('Payment analytics fetched successfully:', response);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching payment analytics:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to fetch payment analytics: ' + (error.message || 'Server error');
    throw error;
  }
};

// Send money to a member
const sendMoneyToMember = async (memberId, transactionData) => {
  try {
    console.log(`Sending money to member ${memberId}:`, {
      ...transactionData,
      amount: `$${transactionData.amount}`
    });
    
    // Get tenant ID using the standardized function
    const tenantId = getValidTenantId();
    
    // Configure request with explicit headers
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    // Make the API request with proper headers
    const response = await api.post(`${ENDPOINT}/${memberId}/transactions`, transactionData, { headers });
    
    // Log success for debugging
    console.log('Money sent successfully:', response);
    
    return response;
  } catch (error) {
    console.error(`Error sending money to member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to send money: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get member's transaction history
const getMemberTransactions = async (memberId) => {
  try {
    console.log(`Fetching transactions for member ${memberId}`);
    
    const response = await api.get(`${ENDPOINT}/${memberId}/transactions`);
    
    // Log success for debugging
    console.log('Transactions fetched successfully:', response);
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching transactions for member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to fetch transactions: ' + (error.message || 'Server error');
    throw error;
  }
};

// Get current member's transactions (for logged-in member)
const getCurrentMemberTransactions = async (config = {}) => {
  try {
    console.log('Fetching current member transactions');
    
    // Get token directly if not provided in config
    const token = config.headers?.Authorization?.replace('Bearer ', '') || localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token available for transactions fetch');
      throw new Error('User identification required to access transactions. Please login again.');
    }
    
    // Build request headers explicitly to ensure authentication
    const headers = {
      ...config.headers,
      'Authorization': `Bearer ${token}`
    };
    
    // Get tenant ID using the standardized function or from config
    const tenantId = config.headers?.['X-Tenant-ID'] || getValidTenantId();
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    // Get user ID from config or localStorage
    const userId = config.headers?.['X-User-ID'] || localStorage.getItem('userId');
    if (userId) {
      headers['X-User-ID'] = userId;
    }
    
    console.log('Making transactions request with headers:', {
      hasAuthToken: !!headers.Authorization,
      tenantId: headers['X-Tenant-ID'] || 'not set',
      userId: headers['X-User-ID'] || 'not set'
    });
    
    // Make the API request to get the transactions
    const response = await api.get(`${ENDPOINT}/me/transactions`, { headers });
    
    // Log success for debugging
    console.log('Member transactions fetched successfully:', response);
    
    // Handle various response formats
    if (response.data && response.data.data) {
      return response.data; // Return the whole response object
    } else if (Array.isArray(response.data)) {
      // If the response is directly an array, wrap it
      return { data: response.data };
    } else if (response.data && response.data.success && response.data.transactions) {
      return { data: response.data.transactions };
    }
    
    // Default response with empty array if no recognized format
    return { data: [] };
  } catch (error) {
    console.error('Error fetching member transactions:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    // Instead of throwing error, return empty data to avoid breaking the UI
    return { data: [], error: error.message };
  }
};

// Get all payments across all members (for admin) with enhanced fallbacks
const getAllPayments = async () => {
  try {
    console.log('Fetching all payments across members');
    
    // Try the primary endpoint with error handling
    try {
      const response = await api.get(`${ENDPOINT}/payments`);
      
      // Log success for debugging
      console.log('All payments fetched successfully from primary endpoint:', response);
      
      return response.data;
    } catch (primaryError) {
      // If there's a 500 error, try alternate endpoints
      console.warn('Error with primary payments endpoint:', primaryError.message);
      
      // Try alternate endpoint: /api/payments
      try {
        console.log('Attempting alternate endpoint: /api/payments');
        const altResponse = await api.get('/payments');
        console.log('Payments fetched from alternate endpoint:', altResponse);
        return altResponse.data;
      } catch (altError) {
        console.warn('Error with alternate payments endpoint:', altError.message);
        
        // Try third alternative endpoint
        try {
          console.log('Attempting third alternative endpoint: /api/finance/payments');
          const thirdResponse = await api.get('/finance/payments');
          console.log('Payments fetched from third endpoint:', thirdResponse);
          return thirdResponse.data;
        } catch (thirdError) {
          console.warn('Error with third payments endpoint:', thirdError.message);
          
          // Let's try one more: /api/transactions (which might contain payment data)
          try {
            console.log('Attempting to fetch from transactions endpoint');
            const transResponse = await api.get(`/transactions`);
            console.log('Transactions fetched as payment fallback:', transResponse);
            return transResponse.data;
          } catch (transError) {
            console.warn('All specific endpoints failed:', transError.message);
            throw primaryError; // Throw the original error
          }
        }
      }
    }
  } catch (error) {
    console.error('All payment endpoints failed:', error);
    
    // Return empty data rather than throwing an error
    // This prevents UI breakage when the API is having issues
    return { data: [], error: 'Failed to fetch payment data: ' + error.message };
  }
};

// Reset a member's password (admin only)
const resetMemberPassword = async (memberId) => {
  try {
    console.log(`Initiating password reset for member ${memberId}`);
    
    // Get tenant ID using the standardized function
    const tenantId = getValidTenantId();
    
    // Configure request with explicit headers
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    
    // Make the API request with proper headers
    const response = await api.post(`${ENDPOINT}/${memberId}/reset-password`, { tenantId }, { headers });
    
    // Log success for debugging (but don't log the actual password)
    console.log('Password reset successfully initiated:', {
      success: response.data?.success,
      hasPasswordData: !!response.data?.data?.tempPassword
    });
    
    return response.data;
  } catch (error) {
    console.error(`Error resetting password for member ${memberId}:`, error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    error.message = 'Failed to reset password: ' + (error.response?.data?.message || error.message || 'Server error');
    throw error;
  }
};

// Add method to get member financial report data
const getMemberFinancialReport = async () => {
  const token = authService.getToken();
  
  try {
    const response = await api.get('/member/financial-report', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching member financial report:', error);
    throw error;
  }
};

// Add method to get all transactions for a tenant
const getTenantTransactions = async () => {
  const token = authService.getToken();
  
  try {
    const response = await api.get('/admin/transactions', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching tenant transactions:', error);
    throw error;
  }
};

// Add method to get all tenant expenses
const getTenantExpenses = async () => {
  const token = authService.getToken();
  
  try {
    const response = await api.get('/admin/expenses', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching tenant expenses:', error);
    throw error;
  }
};

// Bulk upload members from a CSV file
const bulkUploadMembers = async (formData) => {
  try {
    console.log('Uploading members from CSV file');
    
    // Get token directly to ensure proper authentication
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required to bulk upload members');
    }
    
    // Log what we're sending for debugging
    console.log("Uploading member data:", { 
      hasToken: !!token,
      formDataKeys: Array.from(formData.keys())
    });
    
    // IMPORTANT: Create a new custom config to ensure proper handling of FormData
    const config = {
      headers: {
        'Authorization': `Bearer ${token}`,
        // DO NOT set Content-Type here - axios will set the correct one with boundary
      },
      timeout: 30000 // 30 second timeout for large uploads
    };
    
    // Use the API instance with the custom config
    const response = await api.post(`${ENDPOINT}/bulk-upload`, formData, config);
    
    // Log success for debugging
    console.log('Bulk upload successful:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error during bulk upload:', error);
    
    // Add more detailed debugging for API errors
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    
    // Make error message user-friendly
    const errorMessage = 
      'Failed to upload members: ' + (error.response?.data?.message || error.message || 'Server error');
    
    throw new Error(errorMessage);
  }
};

// Financial report scheduling service
const scheduleFinancialReport = async (scheduleData) => {
  try {
    const response = await api.post('/financial-reports/schedule', scheduleData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get all scheduled reports
const getScheduledReports = async () => {
  try {
    const response = await api.get('/financial-reports/schedules');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete a scheduled report
const deleteScheduledReport = async (scheduleId) => {
  try {
    const response = await api.delete(`/financial-reports/schedules/${scheduleId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update a scheduled report
const updateScheduledReport = async (scheduleId, scheduleData) => {
  try {
    const response = await api.put(`/financial-reports/schedules/${scheduleId}`, scheduleData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const memberService = {
  getAllMembers,
  getAllMembersWithHeaders,
  getMember,
  createMember,
  updateMember,
  deleteMember,
  searchMembers,
  addPayment,
  applyPaymentToDebt,
  getCurrentMemberProfile,
  updateCurrentMemberProfile,
  updateMemberAccount,
  getMemberPaymentHistory,
  getMemberByMembershipNumber,
  getDashboardStats,
  getMembershipAnalytics,
  getMemberGrowthTrend,
  getMembershipDistribution,
  getMemberRetentionRate,
  changePassword,
  uploadProfilePicture,
  deleteProfilePicture,
  uploadMemberProfilePicture,
  deleteMemberProfilePicture,
  getDirectoryMembers,
  transferMember,
  addDebt,
  deleteDebt,
  getMemberDebts,
  updateDebtStatus,
  getCurrentMemberDebts,
  bulkAddDebts,
  bulkUpdateDebtStatus,
  bulkDeleteDebts,
  getAllDebts,
  getDebtAnalytics,
  getPaymentAnalytics,
  sendMoneyToMember,
  getMemberTransactions,
  getCurrentMemberTransactions,
  getAllPayments,
  resetMemberPassword,
  getMemberFinancialReport,
  getTenantTransactions,
  getTenantExpenses,
  bulkUploadMembers,
  scheduleFinancialReport,
  getScheduledReports,
  deleteScheduledReport,
  updateScheduledReport
};

export default memberService;